// Article reactions — multi-emoji single-pick poll on an article
// (plan.md V.1).
//
// Distinct from comment reactions (`packages/contracts/src/comments/`)
// in three ways:
//   1. Each user picks ONE kind per article (PK is `(articleId, userId)`,
//      kind is a regular column). Comments allow `like` AND `dislike`
//      simultaneously (PK includes kind).
//   2. Six emoji kinds, not two — covers the sentiment palette plan.md
//      calls for. Slack-style names so the SPA can map them to any
//      emoji set without a translation table.
//   3. Counts are aggregated on read (GROUP BY), not maintained as
//      denormalised counters on `articles`. The table is small per
//      article and the (articleId, kind) index makes the GROUP BY a
//      bounded index scan; we can cache later if hot articles need it.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Closed enum — keep in sync with the CHECK constraint on
// `article_reactions.kind` and the kinds used in routes/route-schemas.ts.
// ---------------------------------------------------------------------------
export const ARTICLE_REACTION_KINDS = [
    'thumbs_up',
    'bulb',
    'heart',
    'tada',
    'thinking',
    'wow',
];
export const ArticleReactionKind = z.enum(ARTICLE_REACTION_KINDS);
// ---------------------------------------------------------------------------
// ArticleReactionCounts — wire shape for per-kind aggregate.
// Every kind appears in the response even when its count is 0, so the
// SPA can render the full emoji palette without a client-side merge of
// missing keys.
// ---------------------------------------------------------------------------
export const ArticleReactionCounts = z.object({
    thumbs_up: z.number().int().nonnegative(),
    bulb: z.number().int().nonnegative(),
    heart: z.number().int().nonnegative(),
    tada: z.number().int().nonnegative(),
    thinking: z.number().int().nonnegative(),
    wow: z.number().int().nonnegative(),
});
/** Zero-counts seed — used by repos that need a "no reactions yet"
 *  baseline to merge GROUP BY results into. Frozen so a caller can't
 *  mutate the canonical zero shape. */
export const EMPTY_REACTION_COUNTS = Object.freeze({
    thumbs_up: 0,
    bulb: 0,
    heart: 0,
    tada: 0,
    thinking: 0,
    wow: 0,
});
// ---------------------------------------------------------------------------
// ArticleReactionState — the full read shape returned by GET / POST /
// DELETE. `total` is the sum across all kinds (handy for an aggregate
// "N people reacted" badge); recomputable on the client but pre-summed
// to spare every consumer that loop.
// ---------------------------------------------------------------------------
export const ArticleReactionState = z.object({
    articleId: z.string().uuid(),
    counts: ArticleReactionCounts,
    total: z.number().int().nonnegative(),
    /** The viewer's own pick, or `null` for guests / non-reactors. The
     *  API never exposes other users' individual picks (that's a
     *  privacy-leak vector and there's no product surface that needs
     *  it; the per-kind counts are the only published aggregation). */
    mine: ArticleReactionKind.nullable(),
});
//# sourceMappingURL=reaction.js.map